import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

// 업데이트와 애널리틱스 패널(드로어) — 댓글/버전 드로어와 동일 톤(.cm-panel 재사용).
// 표시: 조회수(workspace.page_views count), 최근 조회자, 마지막 편집 시각(page.updated_at), 생성일.
// page_views 테이블이 아직 없으면(마이그레이션 전) 방어적으로 빈 집계 + 안내.

const AV_COLORS = ['#2383e2', '#e0561d', '#0f9d58', '#a142f4', '#d93025', '#f4b400', '#1abc9c', '#9b59b6'];
function colorFor(id) {
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}

function timeLabel(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Math.max(0, Date.now() - d.getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return '방금';
  if (m < 60) return `${m}분 전`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}시간 전`;
  const dy = Math.floor(h / 24);
  if (dy < 7) return `${dy}일 전`;
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fullDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

export default function AnalyticsPanel({ page, onClose }) {
  const { membership } = useAuth();
  const orgId = membership?.org_id;

  const [loading, setLoading] = useState(true);
  const [viewsAvailable, setViewsAvailable] = useState(true);
  const [viewCount, setViewCount] = useState(0);
  const [recentViewers, setRecentViewers] = useState([]); // [{ user_id, viewed_at }]
  const [memberMap, setMemberMap] = useState({});

  // 멤버 목록(조회자 이름) — CommentsPanel/VersionHistory 와 동일 패턴.
  useEffect(() => {
    let active = true;
    if (!orgId) return undefined;
    (async () => {
      const { data } = await supabase
        .schema('workspace').from('members')
        .select('user_id, display_name, avatar_url, org_id')
        .eq('org_id', orgId);
      if (!active) return;
      const map = {};
      (data || []).forEach((m) => { map[m.user_id] = m; });
      setMemberMap(map);
    })();
    return () => { active = false; };
  }, [orgId]);

  const load = useCallback(async () => {
    if (!page?.id) return;
    setLoading(true);
    // 총 조회수 — head:true + count 로 행을 받지 않고 개수만.
    const { count, error: countErr } = await supabase
      .schema('workspace').from('page_views')
      .select('id', { count: 'exact', head: true })
      .eq('page_id', page.id);
    if (countErr) {
      // 테이블 없음/RLS → 애널리틱스 비활성으로 안내(방어적).
      setViewsAvailable(false);
      setViewCount(0);
      setRecentViewers([]);
      setLoading(false);
      return;
    }
    setViewsAvailable(true);
    setViewCount(count || 0);
    // 최근 조회자 — 최신 viewed_at 순으로 상위 몇 개, 사용자 중복 제거.
    const { data: rows } = await supabase
      .schema('workspace').from('page_views')
      .select('user_id, viewed_at')
      .eq('page_id', page.id)
      .order('viewed_at', { ascending: false })
      .limit(40);
    const seen = new Set();
    const uniq = [];
    (rows || []).forEach((r) => {
      if (r.user_id && !seen.has(r.user_id)) {
        seen.add(r.user_id);
        uniq.push(r);
      }
    });
    setRecentViewers(uniq.slice(0, 8));
    setLoading(false);
  }, [page?.id]);

  useEffect(() => { load(); }, [load]);

  return (
    <aside className="cm-panel" role="complementary" aria-label="업데이트와 애널리틱스">
      <header className="cm-head">
        <span className="cm-head-title">업데이트와 애널리틱스</span>
        <button type="button" className="cm-close" onClick={onClose} aria-label="애널리틱스 닫기">✕</button>
      </header>

      <div className="cm-list">
        {loading ? (
          <div className="cm-state"><div className="spinner" /></div>
        ) : (
          <>
            {/* 핵심 지표 카드 */}
            <div className="an-stats">
              <div className="an-stat">
                <span className="an-stat-num">{viewsAvailable ? viewCount.toLocaleString('ko-KR') : '—'}</span>
                <span className="an-stat-label">조회수</span>
              </div>
              <div className="an-stat">
                <span className="an-stat-num">{recentViewers.length}</span>
                <span className="an-stat-label">최근 조회자</span>
              </div>
            </div>

            {!viewsAvailable ? (
              <div className="an-note">
                조회 기록(page_views) 테이블이 아직 준비되지 않았어요. 마이그레이션 적용 후 집계가 표시됩니다.
              </div>
            ) : null}

            {/* 업데이트 정보 */}
            <div className="an-section-label">업데이트</div>
            <div className="an-rows">
              <div className="an-row">
                <span className="an-row-key">마지막 편집</span>
                <span className="an-row-val">{timeLabel(page?.updated_at)}</span>
              </div>
              <div className="an-row">
                <span className="an-row-key">생성일</span>
                <span className="an-row-val">{fullDate(page?.created_at)}</span>
              </div>
            </div>

            {/* 최근 조회자 */}
            <div className="an-section-label">최근 조회자</div>
            {viewsAvailable && recentViewers.length > 0 ? (
              <div className="an-viewers">
                {recentViewers.map((v) => {
                  const m = memberMap[v.user_id];
                  const name = m?.display_name || '알 수 없는 사용자';
                  return (
                    <div className="an-viewer" key={v.user_id}>
                      {m?.avatar_url ? (
                        <img className="cm-avatar" src={m.avatar_url} alt={name} />
                      ) : (
                        <span className="cm-avatar" style={{ background: colorFor(v.user_id) }}>
                          {name.slice(0, 1)}
                        </span>
                      )}
                      <div className="an-viewer-body">
                        <span className="an-viewer-name">{name}</span>
                        <span className="an-viewer-time">{timeLabel(v.viewed_at)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="an-empty">{viewsAvailable ? '아직 조회 기록이 없어요.' : '집계 대기 중.'}</div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
